
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import Navbar from "@/components/Navbar";
import { AuthStateHandler } from "@/components/auth/AuthStateHandler";
import { AppRoutes } from "@/components/router/AppRoutes";
import { Toaster } from "@/components/ui/toaster";
import { SiteFooter } from "@/components/SiteFooter";
import { useEffect, useState } from "react";
import { useDarkroomMode, DarkroomModeProvider } from "./hooks/useDarkroomMode";
import { useLocation } from "react-router-dom";
import { Capacitor } from '@capacitor/core';
import { useInAppLinks } from "./hooks/useInAppLinks";
import { useAppUrlListener } from "./utils/appUrlHandler";
import { DynamicIslandHeader } from "@/components/DynamicIslandHeader";

// Define the description at the app level to ensure it's consistently available
export const SAFELIGHT_DESCRIPTION = "A global guide for film photographers: Find and review airports that respect your analog film. Share hand check success stories, learn from others, and travel with confidence.";

function AppContent() {
  const isMobile = useIsMobile();
  const { darkroomMode } = useDarkroomMode();
  const location = useLocation();
  const isDevelopPath = location.pathname === '/develop';
  const [appReady, setAppReady] = useState(false);
  
  // Use our custom hooks for in-app navigation and URL handling
  useInAppLinks();
  useAppUrlListener();

  // Check if running in Capacitor (native app)
  const isCapacitorApp = Capacitor.isNativePlatform();
  
  // Add iOS detection
  const isIOS = (isCapacitorApp && Capacitor.getPlatform() === 'ios') || 
                /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Apply darkroom mode class to body only when on develop page
  useEffect(() => {
    if (darkroomMode && isDevelopPath) {
      document.body.classList.add('darkroom-mode');
    } else {
      document.body.classList.remove('darkroom-mode');
    }
  }, [darkroomMode, isDevelopPath]);
  
  // Handle iOS WebView readiness
  useEffect(() => {
    console.log('App initialization started', { isCapacitorApp, isIOS });
    
    // Slight delay to ensure WebView is fully initialized
    const timer = setTimeout(() => {
      setAppReady(true);
      console.log('App set to ready state');
      
      // Force a repaint on iOS devices to fix black screen issues
      if (isIOS) {
        console.log('Applying iOS repaint fix');
        const html = document.documentElement;
        const originalDisplay = html.style.display;
        html.style.display = 'none';
        setTimeout(() => { 
          html.style.display = originalDisplay;
          console.log('iOS repaint complete'); 
        }, 10);
      }
    }, isIOS ? 500 : 200);
    
    return () => clearTimeout(timer);
  }, [isIOS, isCapacitorApp]);

  if (!appReady) {
    console.log('App not ready yet, showing loading spinner');
    return <div className="flex items-center justify-center h-screen w-screen bg-background">
      <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
    </div>;
  }

  console.log('Rendering main app content', { pathname: location.pathname });
  return (
    <div className={cn(
      "min-h-screen bg-background text-foreground flex flex-col",
      isMobile && "pb-[72px]",
      darkroomMode && isDevelopPath && "bg-black text-red-500"
    )}>
      {/* DynamicIslandHeader is placed here, after theme context is available */}
      <DynamicIslandHeader />
      <AuthStateHandler />
      <Navbar />
      <div className="flex-grow">
        <AppRoutes />
      </div>
      <SiteFooter />
      <Toaster />
    </div>
  );
}

export default function App() {
  console.log('App component mounting');
  return (
    <DarkroomModeProvider>
      <AppContent />
    </DarkroomModeProvider>
  );
}
